/**
 * Contexte de sécurité associant les propriétés minimales du user connecté
 * et d'un objet métier permettant d'évaluer des permissions.
 */
import {ROLES} from "/@lib@/commons/roles.js";

export interface ISecurityCtx {

	/** Liste de roles du user pour l'objet métier de ce contexte. */
	srcRoles: string[];

	/** Droits systèmes de l'objet métier (ex: un item de l'historique est toujours read-only). voir Api Src. */
	srcRi?: ESrcRights;

	/** Le user connecté est-il superAdmin ? (un user superAdmin court-circuite l'évaluation des permissions). */
	isSuperAdmin: boolean;

	/** Account du user connecté. */
	account: string;
}

export interface ISecurityCtxPointer {
	securityCtx?: ISecurityCtx;
}

/** cf com.scenari.src.feature.rights.SrcFeatureRights */
export const enum ESrcRights {
	none = 0,
	read = 1,
	listChildren = 2,
	write = 4,
	remove = 8,
	removeChildren = 16,
	createFile = 32,
	createFolder = 64,
	createChildren = 128,
	move = 256,
	UPDATES = write | remove | removeChildren | createFile | createFolder | createChildren | move,
	READS = read | listChildren,
	ALL = READS | UPDATES
}


export namespace SEC {

	export function createSub(ctx:ISecurityCtx, srcRoles: string | string[], systemRights?: number, businessObject?: any):SecurityCtx {
		return new SecurityCtx((ctx as SecurityCtx).account, srcRoles, ctx.isSuperAdmin, businessObject, systemRights);
	}

	/**
	 * Classe implémentant ISecurityCtx.
	 */
	export class SecurityCtx implements ISecurityCtx {

		/** @deprecated TODOsc7 a suppr.*/
		static createSub = createSub

		srcRoles: string[];
		srcRi: number;
		isSuperAdmin: boolean;
		account: string;

		/** Mémoire de l'objet métier associé (pour debug) */
		businessObject: any;

		/**
		 * @param srcRoles
		 *    - Si "" : aucun rôle de défini dans ce contexte. reg.hasPerm() retournera donc toujours false avec ce contexte.
		 *    - Si string: liste de rôles séparés par des espaces
		 *    - (array)  liste de rôles
		 *    - null ou undefined : le contexte ne gère pas les rôles. Le rôle ~default est donc utilisé
		 */
		constructor(account: string, srcRoles: string | string[], isSuperAdmin?: boolean, businessObject?: any, systemRights?: number) {
			//Public properties
			this.account = account || "";
			if (srcRoles === "") {
				this.srcRoles = [];
			} else if (srcRoles != null) {
				this.srcRoles = Array.isArray(srcRoles) ? srcRoles : srcRoles.split(" ");
			} else {
				this.srcRoles = [DEFAULT_ROLE_NAME];
			}
			if (systemRights != null) this.srcRi = systemRights;
			this.isSuperAdmin = isSuperAdmin === true;
			this.businessObject = businessObject;
		}

		toString() {return `SecurityCtx{account:'${this.account}', businessObject:${this.businessObject}}`}

	}

	/** Préfix des rôles "main". TOut utilisateur *doit* posséder un et un seul rôle main
	 * dans sa lise des rôles résolus */
	export const MAIN_ROLES_PREFIX = "main:";

	/** Rôle global *racine*
	 * Remarque : utilisé également comme rôle courant dans les contextes sans gestion de rôle (ex: SCchain-standalone) */
	export const DEFAULT_ROLE_NAME = "~default";

	export const DEFAULT_ROLES = [DEFAULT_ROLE_NAME];

	/** Rôle par défaut "main" dans les contextes *authentifiés*
	 * Remarque : extend DEFAULT_ROLE_NAME */
	export const FALLBACK_ROLE_NAME = "main:~fallback";

	export const FALLBACK_ROLES = [FALLBACK_ROLE_NAME];

	/** Injecte le rôle FALLBACK_ROLES dans pRoles si la liste des rôles ne possède pas de rôle "main:"
	 * Remarque : cette fonction est à utiliser dans les contextes *avec* gestion de rôles
	 * @see  eu.scenari.commons.security.RolesSet.injectFallback
	 * @param pRoles : liste des rôles à analyser/compléter
	 * @return pRoles ou [FALLBACK_ROLE_NAME] si pRoles est null
	 */
	export function injectFallbackRole(pRoles: string[]): string[] {
		if (!pRoles) return [];
		if (!pRoles.find((role) => role.startsWith(MAIN_ROLES_PREFIX)))
			pRoles.push(FALLBACK_ROLE_NAME)
		return pRoles;
	}

	/*
	 export function getReadableAllowedSystemRights(right: number): string {
	 let result = [];
	 if ((right & 1) == 1) result.push("read");
	 if ((right & 2) == 2) result.push("listChildren");
	 if ((right & 4) == 4) result.push("write");
	 if ((right & 8) == 8) result.push("remove");
	 if ((right & 16) == 16) result.push("onChildren_Remove");
	 if ((right & 32) == 32) result.push("createFile");
	 if ((right & 64) == 64) result.push("createFolder");
	 if ((right & 128) == 128) result.push("onChildren_Create");
	 if ((right & 256) == 256) result.push("move");
	 return result.join(", ");
	 }

	 export function getReadableDeniedSystemRights(right: number): string {
	 let result = [];
	 if ((right & 1) == 0) result.push("read");
	 if ((right & 2) == 0) result.push("listChildren");
	 if ((right & 4) == 0) result.push("write");
	 if ((right & 8) == 0) result.push("remove");
	 if ((right & 16) == 0) result.push("onChildren_Remove");
	 if ((right & 32) == 0) result.push("createFile");
	 if ((right & 64) == 0) result.push("createFolder");
	 if ((right & 128) == 0) result.push("onChildren_Create");
	 if ((right & 256) == 0) result.push("move");
	 return result.join(", ");
	 }
	 */
}
